/**
 * Shipping Protection toggle (Protección de envío)
 *
 * Adds/removes a fixed-price product to the cart based on a toggle whose
 * default comes from the `data-default-on` attribute on the element
 * (currently OFF — the customer opts in). The customer's choice is stored
 * in a cart attribute
 * (`shipping_protection` = "on" | "off") so it survives page reloads and
 * is not forced back on once the customer turns it off.
 *
 * The real charge is the price of the product whose variant ID is configured
 * in Theme settings > Cart > Protección de envío.
 */
(function () {
  if (window.customElements.get('shipping-protection')) return;

  var QTY = 1;

  function root() {
    return (window.Shopify && Shopify.routes && Shopify.routes.root) || '/';
  }

  function postJSON(path, body) {
    return fetch(root() + path, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify(body),
    }).then(function (r) { return r.json(); });
  }

  function getCart() {
    return fetch(root() + 'cart.js', {
      headers: { Accept: 'application/json' },
      cache: 'no-store',
    }).then(function (r) { return r.json(); });
  }

  function refreshThemeCart() {
    document.dispatchEvent(new CustomEvent('theme:cart:refresh', { bubbles: true }));
  }

  class ShippingProtection extends HTMLElement {
    constructor() {
      super();
      this.busy = false;
      this.onToggle = this.onToggle.bind(this);
      this.onCartChange = this.onCartChange.bind(this);
    }

    connectedCallback() {
      this.variantId = parseInt(this.dataset.variantId, 10);
      this.defaultOn = this.dataset.defaultOn !== 'false';
      this.toggle = this.querySelector('[data-shipping-protection-toggle]');

      if (!this.variantId || !this.toggle) return;

      this.toggle.addEventListener('change', this.onToggle);
      document.addEventListener('theme:cart:change', this.onCartChange);

      // Reconcile the cart on first load (this is what makes it "on by default").
      this.sync();
    }

    disconnectedCallback() {
      document.removeEventListener('theme:cart:change', this.onCartChange);
    }

    setBusy(state) {
      this.busy = state;
      this.classList.toggle('is-busy', state);
    }

    onCartChange() {
      // The theme re-rendered the cart (item added/removed/qty changed).
      // Re-reconcile so the protection follows the cart state.
      if (this.busy) return;
      this.sync();
    }

    // Inspect a /cart.js payload relative to the protection variant.
    analyze(cart) {
      var present = false, presentKey = null, presentQty = 0, realItems = 0;
      var items = (cart && cart.items) || [];
      for (var i = 0; i < items.length; i++) {
        var it = items[i];
        if (it.variant_id === this.variantId) {
          present = true;
          presentKey = it.key;
          presentQty = it.quantity;
        } else {
          realItems += it.quantity;
        }
      }
      var choice = (cart && cart.attributes && cart.attributes.shipping_protection) || '';
      return { present: present, presentKey: presentKey, presentQty: presentQty, realItems: realItems, choice: choice };
    }

    wantsOn(info) {
      if (info.choice === 'off') return false;
      if (info.choice === 'on') return true;
      return this.defaultOn; // attribute not set yet -> use default
    }

    // Reconcile cart contents to match desired state, then update the UI.
    sync() {
      var self = this;
      if (this.busy) return Promise.resolve();
      return getCart().then(function (cart) {
        var info = self.analyze(cart);
        var on = self.wantsOn(info);

        // Visibility: only relevant when there is at least one real product.
        self.classList.toggle('cart-sp--hidden', info.realItems === 0);
        // Reflect the toggle visual (does not fire "change").
        self.toggle.checked = on && info.realItems > 0;

        // No real products -> protection must not remain alone in the cart.
        if (info.realItems === 0) {
          if (info.present) return self.mutate(self.removeProtection(info.presentKey));
          return null;
        }

        if (on && !info.present) {
          return self.mutate(self.addProtection());
        }
        if (on && info.present && info.presentQty !== QTY) {
          return self.mutate(self.setQty(info.presentKey, QTY));
        }
        if (!on && info.present) {
          return self.mutate(self.removeProtection(info.presentKey));
        }
        return null;
      }).catch(function () { /* network errors: leave cart untouched */ });
    }

    // Run a mutation promise with busy lock + a single theme refresh after.
    mutate(promise) {
      var self = this;
      this.setBusy(true);
      return promise
        .then(function () {
          self.setBusy(false);
          refreshThemeCart();
        })
        .catch(function () {
          self.setBusy(false);
        });
    }

    addProtection() {
      return postJSON('cart/add.js', { items: [{ id: this.variantId, quantity: QTY }] });
    }

    removeProtection(key) {
      return postJSON('cart/change.js', { id: key, quantity: 0 });
    }

    setQty(key, qty) {
      return postJSON('cart/change.js', { id: key, quantity: qty });
    }

    updateAttr(value) {
      return postJSON('cart/update.js', { attributes: { shipping_protection: value } });
    }

    // Customer flips the switch.
    onToggle() {
      var self = this;
      var on = this.toggle.checked;
      if (this.busy) return;
      this.setBusy(true);

      this.updateAttr(on ? 'on' : 'off')
        .then(function () { return getCart(); })
        .then(function (cart) {
          var info = self.analyze(cart);
          if (on && !info.present && info.realItems > 0) return self.addProtection();
          if (!on && info.present) return self.removeProtection(info.presentKey);
          return null;
        })
        .then(function () {
          self.setBusy(false);
          refreshThemeCart();
        })
        .catch(function () {
          self.setBusy(false);
          // Revert the visual switch on failure.
          self.toggle.checked = !on;
        });
    }
  }

  window.customElements.define('shipping-protection', ShippingProtection);
})();
